/**
 * Notification Settings Page
 * Manage email notification preferences
 * Linkable from emails for unsubscribe
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { ArrowLeft, Bell, Loader2, Mail, Briefcase, AlertTriangle } from "lucide-react";

function ToggleRow({ label, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-white/5 last:border-0">
      <div className="pr-4">
        <p className={`text-sm font-medium ${disabled ? "text-white/40" : "text-white"}`}>{label}</p>
        {description && <p className="text-white/40 text-xs mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full shrink-0 transition-colors ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        } ${checked ? "bg-[#e8644a]" : "bg-white/15"}`}
        data-testid={testId}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    new_jobs: true,
    new_messages: true,
    announcements: true,
    hiring: true,
  });

  // Initialize from user data
  useEffect(() => {
    if (user?.communication_preferences) {
      setPrefs({
        new_jobs: user.communication_preferences.new_jobs !== false,
        new_messages: user.communication_preferences.new_messages !== false,
        announcements: user.communication_preferences.announcements !== false,
        hiring: true, // Always true - required
      });
    }
  }, [user]);

  // Handle unsubscribe from email link
  useEffect(() => {
    const unsubType = searchParams.get("unsubscribe");
    if (unsubType && token) {
      handleToggle(unsubType, false);
    }
  }, [searchParams, token]);

  // Hiring alerts follow from having an active profile, so the control that
  // actually governs them belongs next to the explanation. It otherwise lives
  // only in the avatar menu, which has no URL to point someone at.
  const roles = user?.roles || [];
  const isAssistantRole = roles.includes("assistant");
  const isStudioRole = roles.includes("studio");

  // Active if any role they hold is still switched on. These are the fields the
  // backend filters on — a top-level `is_active` is accepted but never read.
  const roleStates = [];
  if (isAssistantRole) roleStates.push(user?.assistant_profile?.find_work_active !== false);
  if (isStudioRole) roleStates.push(user?.studio_profile?.hiring_active !== false);
  const isActive = roleStates.length === 0 || roleStates.some(Boolean);

  const toggleActive = async () => {
    const nextActive = !isActive;
    const payload = {};
    if (isAssistantRole) {
      payload.assistant_profile = {
        ...user?.assistant_profile,
        find_work_active: nextActive,
      };
    }
    if (isStudioRole) {
      payload.studio_profile = {
        ...user?.studio_profile,
        hiring_active: nextActive,
      };
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Add a profile before changing your visibility");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        updateUser(await res.json());
        toast.success(isActive ? "Your profile is now inactive" : "Your profile is active again");
      } else if (res.status === 429) {
        toast.error("Too many changes at once — wait a minute and try again");
      } else {
        toast.error("Failed to update your profile");
      }
    } catch {
      toast.error("Failed to update your profile");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key, value) => {
    if (key === "hiring") return; // Cannot disable hiring alerts

    const newPrefs = { ...prefs, [key]: value !== undefined ? value : !prefs[key] };
    setPrefs(newPrefs);
    setSaving(true);

    try {
      const res = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          communication_preferences: { ...newPrefs, hiring: true },
        }),
      });

      if (res.ok) {
        const updatedUser = await res.json();
        updateUser(updatedUser);
        toast.success("Notification preferences updated");
      } else if (res.status === 429) {
        setPrefs(prefs);
        toast.error("Too many changes at once — wait a minute and try again");
      } else {
        // Revert on failure
        setPrefs(prefs);
        toast.error("Failed to update preferences");
      }
    } catch {
      setPrefs(prefs);
      toast.error("Failed to update preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Dashboard
        </Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-lg mx-auto">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <Bell className="w-6 h-6 text-white/60" />
            <h1 className="text-2xl font-bold">Notification Settings</h1>
          </div>

          {/* Email Notifications */}
          <Card className="bg-white/[0.03] border-white/10 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-white/50" />
              <h2 className="text-lg font-semibold">Email Notifications</h2>
            </div>
            <p className="text-white/40 text-sm mb-4">
              Choose which emails you'd like to receive. You can update these at any time.
            </p>

            <ToggleRow
              label="New Job Listings"
              description="Get notified when new jobs are posted in your area"
              checked={prefs.new_jobs}
              onChange={() => handleToggle("new_jobs")}
              disabled={saving}
              testId="pref-toggle-new-jobs"
            />
            <ToggleRow
              label="New Messages"
              description="Receive email notifications for new in-app messages"
              checked={prefs.new_messages}
              onChange={() => handleToggle("new_messages")}
              disabled={saving}
              testId="pref-toggle-new-messages"
            />
            <ToggleRow
              label="System Announcements"
              description="Platform updates, new features, and important news"
              checked={prefs.announcements}
              onChange={() => handleToggle("announcements")}
              disabled={saving}
              testId="pref-toggle-announcements"
            />
          </Card>

          {/* Required Notifications */}
          <Card className="bg-white/[0.03] border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#e8644a]" />
              <h2 className="text-lg font-semibold">Required Notifications</h2>
            </div>
            <p className="text-white/40 text-sm mb-4">
              These carry information you need about work you have taken on, so they cannot be
              switched off on their own.
            </p>

            <ToggleRow
              label="Hiring Alerts"
              description="Job offers, confirmations, and contract-related notifications."
              checked={true}
              onChange={() => {}}
              disabled
              testId="pref-toggle-hiring"
            />

            <div className="mt-5 pt-5 border-t border-white/10">
              <h3 className="text-sm font-medium mb-1">If you would rather not be contacted at all</h3>
              <p className="text-white/40 text-sm mb-4">
                Hiring alerts stop when your profile is inactive, because studios can no longer
                find you or offer you work. You have two options:
              </p>

              <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5">
                <div>
                  <p className="text-sm font-medium">Make my profile inactive</p>
                  <p className="text-white/40 text-xs mt-1">
                    Hides you from searches and stops hiring alerts. Your account, profile, and
                    reviews stay exactly as they are, and you can switch this back on at any time.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!isActive}
                  onClick={toggleActive}
                  disabled={saving}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${!isActive ? "bg-[#e8644a]" : "bg-white/20"}`}
                  data-testid="toggle-profile-inactive"
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${!isActive ? "translate-x-6" : "translate-x-1"}`}
                  />
                </button>
              </div>

              <div className="py-3">
                <p className="text-sm font-medium">Deactivate my account</p>
                <p className="text-white/40 text-xs mt-1 mb-3">
                  Closes your account and stops all email except anything legally required.
                  Reversible by contacting us.
                </p>
                <Link to="/profile/edit?tab=account" data-testid="link-deactivate-account">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    Go to account settings
                  </Button>
                </Link>
              </div>
            </div>
          </Card>

          {saving && (
            <div className="flex items-center justify-center gap-2 mt-4 text-white/40">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Saving...</span>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
